import React, { useState, useEffect, useRef, useMemo } from "react"
import { Form, Modal, Spin } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useMatch, useParams, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { getBooking, cleanedBooking, cleanClientId, cleanedExistingClientList, setClientId, refreshClientBookingDetails } from "store/bookings"
import { fetchCommunicationNotificationStatus } from "store/communication"
import BookingForm from "../BookingForm"
import { resetExperienceBookingData, resetExperienceById, fetchExperiences } from "store/experiences"
import { selectedPaymentMethodIdSelector, setSelectedPaymentMethodId, cleanedPaymentMethods } from "store/user"
import { useStripe } from "@stripe/react-stripe-js"
import { submitTripBooking, populateFormWithBooking, hasNoUserBookingChanges } from "../bookingUtils"
import { useDevice } from "hooks/useDevice"
import { ResponsivePage } from "ui/ResponsivePage"
import { routes } from "router"
import DiscardConfirm from "ui/DiscardModal"
import EditBookingForm from "../BookingForm/EditBookingForm"

const GuideBookingWindow = () => {
  const [form] = Form.useForm()
  const { id: bookingUuid } = useParams()
  const location = useLocation()
  const client = location?.state?.client
  const filterExperienceIds = location?.state?.filterExperienceIds // From equipment details page
  const { isMobile } = useDevice()
  const stripe = useStripe()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false)
  const isDirtyRef = useRef(false)
  const [appliedPromos, setAppliedPromos] = useState([])
  const [isOriginalDiscountRemoved, setIsOriginalDiscountRemoved] = useState(false)
  const handlePromoApplied = (promo) => setAppliedPromos((prev) => [...prev, promo])
  const handlePromoRemoved = (code) => setAppliedPromos((prev) => prev.filter((p) => p.code !== code))
  const handleClearPromos = () => setAppliedPromos([])

  const isBookAgain = useMatch(routes.guideBookingBookAgainPath())
  const isEdit = useMatch(routes.guideBookingEditPath())
  const [bookingModalVisible, setBookingModalVisible] = useState(true)
  const [formLoading, setFormLoading] = useState(!!bookingUuid)
  const [retryBookingUuid, setRetryBookingUuid] = useState(null)
  const { booking } = useSelector((store) => store.bookings)
  const clientId = useSelector((state) => state.bookings.clientId)
  const {
    experiences: {
      experienceBookingData: { bookingSelections }
    }
  } = useSelector((state) => state)
  const selectedPaymentMethodId = useSelector(selectedPaymentMethodIdSelector)

  const originalPromo = useMemo(() => {
    if (!isEdit) return null
    // The discount RULE comes from booking.discount_details (booking detail API). Fall back to
    // payment_summary for older shapes.
    const detail = booking?.discount_details
    const paymentSummary = booking?.payment_summary ?? {}
    const code = detail?.code ?? paymentSummary?.discount_code
    if (!code) return null
    const value = detail?.value ?? paymentSummary?.discount_value
    const discountType = detail?.discount_type ?? paymentSummary?.discount_type
    const applyToAddOns =
      detail?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.applicability_rules?.apply_to_add_ons ??
      paymentSummary?.apply_to_add_ons ??
      false
    const label =
      discountType === "percentage"
        ? `${value}% off`
        : `$${Number(value ?? paymentSummary?.discount_amount ?? 0).toFixed(2)} off`
    const base = { type: "discount", code, name: detail?.name || code, label }

    // Until the user changes guests/add-ons, show the discount the backend actually applied
    // (payment_summary.discount_amount) so it matches the saved total exactly — recomputing it on
    // the frontend can drift. Once something changes, carry the RULE so the discount recomputes
    // against the new base (guest subtotal + add-ons, per apply_to_add_ons).
    const ready = !!bookingSelections?.counts
    const unchanged = !ready || hasNoUserBookingChanges(bookingSelections, booking)
    if (unchanged || value == null || !discountType) {
      return { ...base, discountAmount: Math.max(0, Number(paymentSummary?.discount_amount || 0)) }
    }
    return { ...base, value: Number(value), discount_type: discountType, apply_to_add_ons: applyToAddOns }
  }, [isEdit, booking, bookingSelections])

  const effectiveAppliedPromos = useMemo(() => {
    if (!isEdit || isOriginalDiscountRemoved || !originalPromo) return appliedPromos
    return [originalPromo, ...appliedPromos]
  }, [isEdit, isOriginalDiscountRemoved, originalPromo, appliedPromos])

  const handleEditPromoRemoved = (code) => {
    if (isEdit && code === booking?.payment_summary?.discount_code && !isOriginalDiscountRemoved) {
      setIsOriginalDiscountRemoved(true)
    } else {
      handlePromoRemoved(code)
    }
  }

  const handleCancel = () => {
    if (isDirtyRef.current) {
      setShowDiscardConfirm(true)
    } else {
      closeForm()
    }
  }

  const closeForm = () => {
    isDirtyRef.current = false
    form.resetFields()
    dispatch(cleanClientId())
    dispatch(cleanedPaymentMethods())
    dispatch(setSelectedPaymentMethodId(""))
    dispatch(resetExperienceBookingData())
    dispatch(resetExperienceById())
    dispatch(cleanedExistingClientList())
    dispatch(cleanedBooking())
    setBookingModalVisible(false)
    navigate(-1)
  }

  const handleDiscardCancel = () => {
    setShowDiscardConfirm(false)
  }

  const handleDiscardConfirm = () => {
    setShowDiscardConfirm(false)
    closeForm()
  }

  const handleSubmit = async () => {
    setFormLoading(true)
    try {
      await submitTripBooking({
        form,
        dispatch,
        stripe,
        bookingSelections,
        selectedPaymentMethodId,
        bookingUuid: retryBookingUuid || bookingUuid,
        isBookAgain,
        isEdit,
        clientId,
        client,
        booking,
        appliedPromos
      })

      // Clear retryBookingUuid only if booking is successful
      setRetryBookingUuid(null)

      // The details view we return to reads `clientBookingDetails` (the /booking_detail
      // endpoint), which the update did not refresh. Re-fetch it — with retry, since that
      // endpoint can briefly return the pre-update snapshot — so it shows the latest data
      // instead of staying stale until a manual refresh. Awaited so the slice is fresh before
      // we navigate back. (Skip for book-again, which creates a separate new booking.)
      if (isEdit && !isBookAgain && bookingUuid) {
        await dispatch(refreshClientBookingDetails(bookingUuid))
      }

      // Close modal or reset as needed
      closeForm()
    } catch (err) {
      if (err?.retryBookingUuid) {
        setRetryBookingUuid(err?.retryBookingUuid)
      } else {
        console.error("Booking failed:", err)
      }
    }

    setFormLoading(false)
  }

  useEffect(() => {
    return () => {
      dispatch(cleanClientId())
      dispatch(cleanedPaymentMethods())
      dispatch(setSelectedPaymentMethodId(""))
      dispatch(resetExperienceBookingData())
      dispatch(resetExperienceById())
      dispatch(cleanedExistingClientList())
      dispatch(cleanedBooking())
    }
  }, [dispatch])

  // Get experiences list and notification status for create mode
  useEffect(() => {
    if (!bookingUuid || isBookAgain) {
      dispatch(fetchExperiences("booking_window"))
      dispatch(fetchCommunicationNotificationStatus())
    }
  }, [bookingUuid, isBookAgain, dispatch])

  useEffect(() => {
    const fetchAndPopulate = async () => {
      if (!bookingUuid) return
      dispatch(cleanedBooking())
      dispatch(resetExperienceById())
      dispatch(resetExperienceBookingData())
      form.resetFields()
      setFormLoading(true)

      try {
        const [res, notificationRes] = await Promise.all([
          dispatch(getBooking(bookingUuid, true, isBookAgain)),
          dispatch(fetchCommunicationNotificationStatus())
        ])
        const bookingData = res?.payload || res

        if (bookingData) {
          await populateFormWithBooking({ booking: bookingData, bookingUuid, dispatch, form, isBookAgain })
          if (!isBookAgain) {
            const isLocked = bookingData?.booking_source === "marketplace" || bookingData?.booking_source === "website"
            const notifyDefault = isLocked ? true : (notificationRes?.data?.edit_booking_email_allowed ?? true)
            form.setFieldsValue({ client_notify: notifyDefault, send_guest_email: notifyDefault })
          }
          isDirtyRef.current = false
        } else {
          console.warn("No booking returned to populate")
        }
      } catch (err) {
        console.error("Error in fetchAndPopulate", err)
      } finally {
        setFormLoading(false)
      }
    }

    fetchAndPopulate()
  }, [bookingUuid, isBookAgain, dispatch, form])

  useEffect(() => {
    if (client?.user_id) {
      form.setFieldsValue({
        firstName: client?.user?.first_name,
        lastName: client?.user?.last_name,
        email: client?.user?.email,
        phone_number: { fullNumber: client?.user?.phone_number, countryCode: client?.user?.country_code }
      })
      dispatch(setClientId(client?.user_id))
    }
  }, [dispatch, form, client])

  const handleValuesChange = () => {
    isDirtyRef.current = true
  }
  const title = isEdit && !isBookAgain ? t("bookings.edit") : t("bookings.add")

  return (
    <>
      {isMobile ? (
        <ResponsivePage onBackClick={handleCancel} pageTitle={title}>
          <Spin spinning={formLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
              />
            )}
          </Spin>
        </ResponsivePage>
      ) : (
        <Modal
          width={1000}
          title={title}
          open={bookingModalVisible}
          onCancel={handleCancel}
          footer={null}
          maskClosable={false}
          closable={true}
        >
          <Spin spinning={formLoading}>
            {isEdit ? (
              <EditBookingForm
                form={form}
                bookingUuid={bookingUuid}
                isEdit={isEdit}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                onValuesChange={handleValuesChange}
                appliedPromos={effectiveAppliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handleEditPromoRemoved}
              />
            ) : (
              <BookingForm
                form={form}
                bookingUuid={bookingUuid}
                bookAgain={isBookAgain}
                booking={booking}
                onSubmit={handleSubmit}
                onCancelModal={handleCancel}
                bookingModalVisible={bookingModalVisible}
                filterExperienceIds={filterExperienceIds}
                appliedPromos={appliedPromos}
                onPromoApplied={handlePromoApplied}
                onPromoRemoved={handlePromoRemoved}
                onClearPromos={handleClearPromos}
                onValuesChange={handleValuesChange}
              />
            )}
          </Spin>
        </Modal>
      )}

      {/* Discard Confirmation Modal */}
      <DiscardConfirm
        open={showDiscardConfirm}
        onCancel={handleDiscardCancel}
        onConfirm={handleDiscardConfirm}
        title={t("Discard Changes?")}
        description={t("global.discard_changes")}
        t={t}
      />
    </>
  )
}

export default GuideBookingWindow
